import React, { useCallback, useContext, useEffect, useMemo } from "react";
import {
  NetworkStatus,
  QueryHookOptions,
  useMutation,
  useQuery,
} from "@apollo/client";
import { USER_CONTEXT_QUERY } from "@/graphql/queries/common/UserContextQuery.ts";
import { UserContext, UserContextShape } from "@/helpers/userContext.ts";
import {
  generatePath,
  matchPath,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { PROJECT_URL, PROJECTS_URL } from "@/helpers/url";
import { isGlobalId } from "@/helpers/utils";
import {
  ADD_PROJECT_TO_USER_RECENT_PROJECTS_MUTATION,
  CURRENT_PROJECT_DETAILS_QUERY,
  VENDOR_PROJECT_SEARCH_QUERY,
} from "@/graphql/queries/ProjectSearchQuery";
import {
  AddProjectToUserRecentProjectsMutation,
  AddProjectToUserRecentProjectsMutationVariables,
  CompanyRole,
  CurrentProjectDetailsQuery,
  CurrentProjectDetailsQueryVariables,
  VendorProjectSearchQuery,
  VendorProjectSearchQueryVariables,
} from "@/__generated__/graphql";
import useStaleness from "@/helpers/useStaleness";
import { recordNavigation } from "@/helpers/pageNavigationTracker";

interface UserContextProviderProps {
  children: React.ReactNode;
  skip?: boolean;
}

export default function UserContextProvider({
  children,
  skip = false,
}: UserContextProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const pastMidnight = useStaleness();

  useEffect(() => {
    recordNavigation(location.pathname);
  }, [location.pathname]);

  const currentProjectId = useMemo(
    () => getCurrentProjectId(location.pathname),
    [location.pathname],
  );

  const {
    loading: userLoading,
    error,
    data: userData,
    refetch,
    networkStatus,
  } = useQuery(USER_CONTEXT_QUERY, {
    skip,
  });

  const refetching = networkStatus === NetworkStatus.refetch;
  const isAuthenticated = userData?.isAuthenticated || false;
  const assistantEnabled = userData?.assistantEnabled ?? false;
  const currentUser = userData?.currentUser ?? null;
  const systemAnnouncements = userData?.announcements ?? [];
  const isSupervisor = currentUser?.isSupervisor;
  const userCanBeAssignedTasks = currentUser?.canBeAssignedTasks;

  useEffect(() => {
    if (!currentUser?.savePageViewSettings || !currentProjectId) return;

    const projectPath = `/projects/${encodeURIComponent(currentProjectId)}`;
    const currentProjectPath =
      location.pathname.match(/^\/projects\/[^/]+/)?.[0];
    if (currentProjectPath && currentProjectPath !== projectPath) {
      navigate(
        `${projectPath}${location.pathname.slice(currentProjectPath.length)}${location.search}`,
        { replace: true },
      );
    }
  }, [
    currentProjectId,
    currentUser?.savePageViewSettings,
    location.pathname,
    location.search,
    navigate,
  ]);

  const { data: currentProjectData, loading: currentProjectLoading } =
    useProjectDetail(currentProjectId, {
      skip: isSupervisor || userCanBeAssignedTasks,
    });

  const {
    data: currentVendorProjectData,
    loading: currentVendorProjectLoading,
  } = useVendorProjectSearchResult(currentProjectId, {
    skip: isSupervisor || !userCanBeAssignedTasks,
  });

  const [addProjectToUserRecentProjects] = useAddProjectToUserRecentProjects();

  const currentProject = currentProjectData?.project || null;
  const currentVendorProject = currentVendorProjectData?.vendorProject || null;

  const onProjectChange = useCallback(
    (projectId: string | null) => {
      if (projectId) {
        if (currentProjectId) {
          const encodedCurrentProjectId = encodeURIComponent(currentProjectId);
          const navigateUrl = location.pathname.replace(
            location.pathname.includes(encodedCurrentProjectId)
              ? encodedCurrentProjectId
              : currentProjectId,
            encodeURIComponent(projectId),
          );
          navigate(navigateUrl);
        } else {
          navigate(
            generatePath(PROJECT_URL, { projectId }) +
              location.pathname.replace(PROJECTS_URL, ""),
          );
        }
      } else {
        if (!currentProjectId) return;
        const encodedCurrentProjectId = encodeURIComponent(currentProjectId);
        navigate(
          location.pathname.replace(
            location.pathname.includes(encodedCurrentProjectId)
              ? `/projects/${encodedCurrentProjectId}`
              : `/projects/${currentProjectId}`,
            "",
          ),
        );
      }
    },
    [currentProjectId, location.pathname, navigate],
  );

  useEffect(() => {
    if (currentUser && !userCanBeAssignedTasks && currentProjectId) {
      addProjectToUserRecentProjects({
        variables: { projectId: currentProjectId },
      });
    }
  }, [
    addProjectToUserRecentProjects,
    currentProjectId,
    currentUser,
    userCanBeAssignedTasks,
  ]);

  const contextValue = useMemo(() => {
    const office = currentUser?.office ?? null;
    const role = office?.company.role ?? null;
    const isHotspringVendorProfile =
      role === CompanyRole.Vendor &&
      currentUser?.office?.company?.profile?.isDefault;
    const isHotspringClientProfile =
      role === CompanyRole.Client &&
      currentUser?.subcontractingUser?.office?.company.profile.isDefault;

    return {
      isAuthenticated,
      currentUser,
      systemAnnouncements,
      office,
      role,
      error,
      loading:
        userLoading || currentProjectLoading || currentVendorProjectLoading,
      refetchUser: refetch,
      refetchingUser: refetching,
      currentProject,
      currentVendorProject,
      setCurrentProject: onProjectChange,
      isHotspringVendorProfile,
      isHotspringClientProfile,
      pastMidnight,
      assistantEnabled,
    };
  }, [
    currentUser,
    isAuthenticated,
    systemAnnouncements,
    error,
    userLoading,
    currentProjectLoading,
    currentVendorProjectLoading,
    refetch,
    refetching,
    currentProject,
    currentVendorProject,
    onProjectChange,
    pastMidnight,
    assistantEnabled,
  ]);

  return (
    <UserContext.Provider value={contextValue}>
      {contextValue.loading ? null : children}
    </UserContext.Provider>
  );
}

export const useUserContext = (): UserContextShape => {
  const context = useContext(UserContext);
  if (!context)
    throw new Error("useUserContext must be used within a UserContextProvider");
  return context;
};

function getCurrentProjectId(pathname: string) {
  const pathMatch = matchPath({ path: PROJECT_URL, end: false }, pathname);
  const id = pathMatch?.params?.projectId;
  if (id) {
    let decodedId = id;
    for (let attempt = 0; attempt < 3 && !isGlobalId(decodedId); attempt += 1) {
      const nextId = decodeURIComponent(decodedId);
      if (nextId === decodedId) break;
      decodedId = nextId;
    }
    if (isGlobalId(decodedId)) {
      return decodedId;
    }
  }
  return null;
}

function useProjectDetail(
  projectId?: string | null,
  { skip }: Pick<QueryHookOptions, "skip"> = {},
) {
  return useQuery<
    CurrentProjectDetailsQuery,
    CurrentProjectDetailsQueryVariables
  >(CURRENT_PROJECT_DETAILS_QUERY, {
    skip: skip || !projectId,
    variables: {
      projectId: projectId ?? "",
    },
    fetchPolicy: "no-cache",
  });
}

function useVendorProjectSearchResult(
  projectId?: string | null,
  { skip }: Pick<QueryHookOptions, "skip"> = {},
) {
  return useQuery<VendorProjectSearchQuery, VendorProjectSearchQueryVariables>(
    VENDOR_PROJECT_SEARCH_QUERY,
    {
      skip: skip || !projectId,
      variables: {
        projectId: projectId ?? "",
      },
      fetchPolicy: "no-cache",
    },
  );
}

function useAddProjectToUserRecentProjects() {
  return useMutation<
    AddProjectToUserRecentProjectsMutation,
    AddProjectToUserRecentProjectsMutationVariables
  >(ADD_PROJECT_TO_USER_RECENT_PROJECTS_MUTATION);
}
